import { useEffect, useState, useCallback } from "react";
import DOMPurify from "dompurify";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Plus, Trash2, Save, FileText, Mail, Eye, CheckCircle2, Image as ImageIcon, FileCog, Upload, Send } from "lucide-react";
import { toast } from "sonner";

const COMMON_SAMPLE = { "[Nombre_Vendedor]": "María González", "[Empresa]": "West KAC Company", "[Fecha]": "18/06/2026" };
const KIND_SAMPLES = {
  cotizacion: { "[Nombre_Cliente]": "Juan Pérez", "[Folio]": "COT-2026-001", "[Monto]": "$12,345.00 MXN", "[Nombre_Vendedor]": "María González", "[Enlace_Aceptacion]": "#" },
  aceptada: { "[Nombre_Cliente]": "Juan Pérez", "[Folio]": "PED-2026-001", "[PO]": "OC-9931", "[Monto]": "$12,345.00 MXN" },
  preparacion: { "[Nombre_Cliente]": "Juan Pérez", "[Folio]": "PED-2026-001", "[PO]": "OC-9931", "[Monto]": "$12,345.00 MXN", "[Direccion_Envio]": "Av. Reforma 123, Col. Centro, CP 25000, Saltillo, Coahuila", "[Datos_Fiscales]": "Cliente Ejemplo S.A. · RFC: XAXX010101000 · Régimen: 601 · CP: 25000" },
  enviado: { "[Nombre_Cliente]": "Juan Pérez", "[Folio]": "PED-2026-001", "[Paqueteria]": "DHL", "[Guia]": "1234567890", "[Enlace_Rastreo]": "#" },
  completado: { "[Nombre_Cliente]": "Juan Pérez", "[Folio]": "PED-2026-001" },
};
const BTN = { "[Boton_Aceptacion]": `<a href="#" style="display:inline-block;background:#2563EB;color:#fff;padding:12px 26px;text-decoration:none;border-radius:6px;font-weight:bold;margin:12px 0">Aceptar cotización</a>`, "[Boton_Rastreo]": `<a href="#" style="display:inline-block;background:#2563EB;color:#fff;padding:12px 24px;text-decoration:none;border-radius:6px">Rastrear mi pedido</a>` };

const renderShell = (inner, useHeader, useFooter, branding) => {
  const logo = (branding && branding.logo_url) || `${window.location.origin}/logo.png`;
  const tagline = (branding && branding.tagline) || "Simplifying the way you work.";
  const footer = (branding && branding.footer_text) || "West KAC Company · (899)-149 1522 · ventas@west-kac.com · west-kac.com";
  const header = useHeader ? `<tr><td style="background:#0F172A;padding:22px 28px" align="center"><img src="${logo}" alt="West KAC" width="210" style="display:block;margin:0 auto;background:#fff;padding:8px 14px;border-radius:8px"/><div style="color:#E68422;font-size:12px;font-style:italic;font-weight:bold;margin-top:10px">${tagline}</div></td></tr>` : "";
  const foot = useFooter ? `<tr><td style="background:#0F172A;padding:16px 28px;color:#94a3b8;font-size:12px;text-align:center">${footer}</td></tr>` : "";
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f3f4f6;padding:24px 0;font-family:Arial,Helvetica,sans-serif"><tr><td align="center"><table role="presentation" width="600" cellpadding="0" cellspacing="0" style="background:#fff;border:1px solid #e5e7eb;border-radius:12px;overflow:hidden">${header}<tr><td style="padding:28px;color:#0A0A0A;font-size:14px;line-height:1.6">${inner}</td></tr>${foot}</table></td></tr></table>`;
};

const buildEmailPreview = (kind, html, useHeader, useFooter, branding, customMap) => {
  const map = { ...COMMON_SAMPLE, ...(customMap || {}), ...(KIND_SAMPLES[kind] || {}), ...BTN };
  let inner = html || "";
  Object.entries(map).forEach(([k, v]) => { inner = inner.split(k).join(v); });
  return renderShell(inner, useHeader, useFooter, branding);
};

// ---------------- Correos por estatus ----------------
function EmailManager({ branding, customMap }) {
  const [kinds, setKinds] = useState({});
  const [kind, setKind] = useState("cotizacion");
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);

  const blank = useCallback((k) => ({ id: null, kind: k, nombre: "", subject: "", html: "", use_header: true, use_footer: true }), []);

  const loadList = useCallback((k) => {
    api.get(`/email-templates?kind=${k}`).then((r) => {
      setList(r.data);
      const active = r.data.find((t) => t.is_active) || r.data[0];
      setEditing(active ? { ...active } : blank(k));
    });
  }, [blank]);

  useEffect(() => { api.get("/email-kinds").then((r) => setKinds(r.data)); }, []);
  useEffect(() => { loadList(kind); }, [kind, loadList]);

  const save = async () => {
    if (!editing.nombre || !editing.subject || !editing.html) return toast.error("Nombre, asunto y cuerpo son obligatorios");
    try {
      if (editing.id) { await api.put(`/email-templates/${editing.id}`, editing); toast.success("Plantilla actualizada"); }
      else { const r = await api.post("/email-templates", editing); toast.success("Plantilla creada"); setEditing({ ...r.data, is_active: false }); }
      loadList(kind);
    } catch (e) { toast.error(apiError(e)); }
  };
  const activate = async (id) => { try { await api.put(`/email-templates/${id}/activate`); toast.success("Plantilla seleccionada para envío"); loadList(kind); } catch (e) { toast.error(apiError(e)); } };
  const testSend = async () => {
    if (!editing.subject || !editing.html) return toast.error("Asunto y cuerpo son obligatorios");
    try {
      const r = await api.post("/email-templates/test-send", { kind, subject: editing.subject, html: editing.html, use_header: editing.use_header, use_footer: editing.use_footer });
      toast.success(`Correo de prueba enviado a ${r.data.to} (${r.data.status})`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => { try { await api.delete(`/email-templates/${id}`); toast.success("Eliminada"); loadList(kind); } catch (e) { toast.error(apiError(e)); } };

  const placeholders = (kinds[kind] && kinds[kind].placeholders) || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-sm">Tipo de correo</Label>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-64" data-testid="email-kind-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(kinds).map(([k, meta]) => <SelectItem key={k} value={k} data-testid={`email-kind-${k}`}>{meta.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => setEditing(blank(kind))} data-testid="new-email-template"><Plus className="w-4 h-4 mr-1" /> Nueva plantilla</Button>
      </div>

      {/* Plantillas guardadas */}
      <div className="flex flex-wrap gap-2" data-testid="email-template-list">
        {list.map((t) => (
          <div key={t.id} className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-sm ${editing && editing.id === t.id ? "border-primary bg-primary/5" : "border-border"}`}>
            <button className="font-medium" onClick={() => setEditing({ ...t })} data-testid={`email-template-${t.id}`}>{t.nombre}</button>
            {t.is_active
              ? <Badge className="bg-emerald-600 hover:bg-emerald-600"><CheckCircle2 className="w-3 h-3 mr-1" /> En uso</Badge>
              : <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => activate(t.id)} data-testid={`activate-${t.id}`}>Usar</Button>}
            <button onClick={() => remove(t.id)} data-testid={`delete-${t.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-5 rounded-lg space-y-4">
            <div className="text-xs text-muted-foreground">Etiquetas: {placeholders.map((p) => <code key={p} className="mx-0.5 px-1.5 py-0.5 bg-muted rounded">{p}</code>)}</div>
            <div><Label>Nombre de la plantilla</Label><Input value={editing.nombre} onChange={(e) => setEditing({ ...editing, nombre: e.target.value })} placeholder="Ej. Cotización formal" data-testid="email-nombre" /></div>
            <div><Label>Asunto</Label><Input value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} data-testid="email-subject" /></div>
            <div><Label>Cuerpo (HTML)</Label><Textarea value={editing.html} onChange={(e) => setEditing({ ...editing, html: e.target.value })} rows={13} className="font-mono text-xs" data-testid="email-html" /></div>
            <div className="flex items-center justify-between border rounded-lg px-3 py-2"><div><div className="text-sm font-medium">Usar encabezado (logo + eslogan)</div><div className="text-xs text-muted-foreground">Desactívalo para enviar solo el cuerpo.</div></div><Switch checked={editing.use_header} onCheckedChange={(v) => setEditing({ ...editing, use_header: v })} data-testid="switch-header" /></div>
            <div className="flex items-center justify-between border rounded-lg px-3 py-2"><div><div className="text-sm font-medium">Usar pie de página (contacto)</div></div><Switch checked={editing.use_footer} onCheckedChange={(v) => setEditing({ ...editing, use_footer: v })} data-testid="switch-footer" /></div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={save} data-testid="save-email-template"><Save className="w-4 h-4 mr-2" /> {editing.id ? "Guardar cambios" : "Crear plantilla"}</Button>
              <Button variant="outline" onClick={testSend} data-testid="test-send-email"><Send className="w-4 h-4 mr-2" /> Enviar correo de prueba</Button>
            </div>
            <p className="text-xs text-muted-foreground">El correo de prueba se envía a <b>{`support.service@west-kac.com`}</b> con datos de ejemplo.</p>
          </Card>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-2"><Eye className="w-4 h-4" /> Vista previa en vivo</div>
            <div className="border border-border rounded-lg overflow-hidden bg-muted/30 max-h-[70vh] overflow-y-auto" data-testid="email-preview">
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(buildEmailPreview(kind, editing.html, editing.use_header, editing.use_footer, branding, customMap), { WHOLE_DOCUMENT: true, ADD_TAGS: ["style"], ADD_ATTR: ["target"] }) }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Marca y logo ----------------
function BrandManager({ branding, reload }) {
  const [tagline, setTagline] = useState("");
  const [footer, setFooter] = useState("");
  const [uploading, setUploading] = useState(false);
  useEffect(() => { setTagline(branding.tagline || ""); setFooter(branding.footer_text || ""); }, [branding]);

  const saveText = async () => { try { await api.put("/branding", { tagline, footer_text: footer }); toast.success("Marca actualizada"); reload(); } catch (e) { toast.error(apiError(e)); } };
  const onUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Logo actualizado. Se aplicará en correos y PDF."); reload(); }
    catch (err) { toast.error(apiError(err)); } finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card className="p-5 rounded-lg space-y-4">
        <h3 className="font-heading font-semibold flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Logo corporativo</h3>
        <p className="text-sm text-muted-foreground">Este logo se usa en el encabezado de los correos y en el PDF de cotización.</p>
        <div className="border rounded-lg p-4 bg-[#0F172A] flex justify-center">
          <img src={`${branding.logo_url}${branding.logo_url && branding.logo_url.includes("?") ? "" : `?t=${Date.now()}`}`} alt="Logo actual" style={{ maxHeight: 70, background: "#fff", padding: "6px 12px", borderRadius: 8 }} data-testid="logo-preview" />
        </div>
        <Label htmlFor="logo-file" className="inline-flex items-center gap-2 cursor-pointer border rounded-lg px-4 py-2 text-sm hover:bg-muted" data-testid="logo-upload-label"><Upload className="w-4 h-4" /> {uploading ? "Subiendo..." : "Cargar nuevo logo (PNG)"}</Label>
        <input id="logo-file" type="file" accept="image/png,image/jpeg" className="hidden" onChange={onUpload} data-testid="logo-file-input" />
      </Card>
      <Card className="p-5 rounded-lg space-y-4">
        <h3 className="font-heading font-semibold">Textos de marca (correos)</h3>
        <div><Label>Eslogan (bajo el logo)</Label><Input value={tagline} onChange={(e) => setTagline(e.target.value)} data-testid="brand-tagline" /></div>
        <div><Label>Pie de página (contacto)</Label><Textarea value={footer} onChange={(e) => setFooter(e.target.value)} rows={3} data-testid="brand-footer" /></div>
        <Button onClick={saveText} data-testid="save-brand"><Save className="w-4 h-4 mr-2" /> Guardar textos</Button>
      </Card>
    </div>
  );
}

// ---------------- Formato PDF ----------------
function PdfManager({ pdf, reload, fonts }) {
  const [cfg, setCfg] = useState(pdf);
  useEffect(() => { setCfg(pdf); }, [pdf]);
  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  const save = async () => { try { await api.put("/branding", { pdf_config: cfg }); toast.success("Formato PDF guardado"); reload(); } catch (e) { toast.error(apiError(e)); } };
  const preview = async () => {
    try { await api.put("/branding", { pdf_config: cfg }); const r = await api.get("/branding/pdf-preview", { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const colorRow = (label, key) => (
    <div className="flex items-center justify-between"><Label>{label}</Label><input type="color" value={cfg[key] || "#000000"} onChange={(e) => set(key, e.target.value)} className="h-9 w-16 rounded border cursor-pointer" data-testid={`pdf-${key}`} /></div>
  );
  const switchRow = (label, key) => (
    <div className="flex items-center justify-between border rounded-lg px-3 py-2"><div className="text-sm font-medium">{label}</div><Switch checked={!!cfg[key]} onCheckedChange={(v) => set(key, v)} data-testid={`pdf-${key}`} /></div>
  );

  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 rounded-lg space-y-4">
          <h3 className="font-heading font-semibold flex items-center gap-2"><FileCog className="w-4 h-4" /> Estilo general del PDF</h3>
          <p className="text-xs text-muted-foreground">El "Tipo de letra" se aplica al resto del documento (título, tabla, totales). El formato del editor enriquecido tiene prioridad sobre el texto seleccionado.</p>
          {colorRow("Color del título", "title_color")}
          {colorRow("Color de acento", "accent_color")}
          {colorRow("Color de la línea del encabezado", "line_color")}
          <div><Label>Tipo de letra (resto del PDF)</Label>
            <Select value={cfg.font} onValueChange={(v) => set("font", v)}>
              <SelectTrigger data-testid="pdf-font"><SelectValue placeholder="Selecciona una fuente" /></SelectTrigger>
              <SelectContent>
                {fonts.map((f) => <SelectItem key={f.value} value={f.value}><span style={{ fontFamily: f.css }}>{f.label}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </Card>
        <Card className="p-5 rounded-lg space-y-4">
          <h3 className="font-heading font-semibold">Pie de página y visibilidad</h3>
          <div><Label>Texto del pie de página</Label><Input value={cfg.footer_text || ""} onChange={(e) => set("footer_text", e.target.value)} data-testid="pdf-footer" /></div>
          {switchRow("Mostrar logo", "show_logo")}
          {switchRow("Mostrar eslogan", "show_tagline")}
          {switchRow("Mostrar datos fiscales", "show_fiscal")}
          {switchRow("Mostrar numeración de páginas", "show_page_numbers")}
        </Card>
      </div>

      <Card className="p-5 rounded-lg space-y-3">
        <div><Label>Eslogan corporativo (editor enriquecido)</Label>
          <p className="text-xs text-muted-foreground mb-2">Selecciona texto y aplica fuente, tamaño, color, negrita/cursiva/subrayado, alineación, listas o enlaces.</p>
          <RichTextEditor value={cfg.tagline || ""} onChange={(html) => set("tagline", html)} fonts={fonts} minHeight={80} testid="pdf-tagline-rte" />
        </div>
        <div><Label>Datos fiscales (editor enriquecido)</Label>
          <p className="text-xs text-muted-foreground mb-2">Aparece bajo el logo en el encabezado del PDF.</p>
          <RichTextEditor value={cfg.fiscal_data || ""} onChange={(html) => set("fiscal_data", html)} fonts={fonts} minHeight={110} testid="pdf-fiscal-rte" />
        </div>
      </Card>

      <div className="flex gap-2">
        <Button onClick={save} data-testid="save-pdf"><Save className="w-4 h-4 mr-2" /> Guardar formato</Button>
        <Button variant="outline" onClick={preview} data-testid="preview-pdf"><Eye className="w-4 h-4 mr-2" /> Guardar y ver PDF</Button>
      </div>
    </div>
  );
}

// ---------------- Etiquetas personalizadas ----------------
function CustomPlaceholderManager({ list, reload }) {
  const [rows, setRows] = useState(list || []);
  useEffect(() => { setRows(list || []); }, [list]);
  const add = () => setRows((r) => [...r, { clave: "", valor: "" }]);
  const upd = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const del = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const save = async () => {
    const norm = rows.map((r) => ({ clave: (r.clave || "").trim(), valor: r.valor || "" })).filter((r) => r.clave)
      .map((r) => ({ ...r, clave: r.clave.startsWith("[") ? r.clave : `[${r.clave.replace(/[[\]]/g, "")}]` }));
    try { await api.put("/branding", { custom_placeholders: norm }); toast.success("Etiquetas personalizadas guardadas"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="p-5 rounded-lg space-y-3" data-testid="custom-ph-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div><h3 className="font-heading font-semibold">Etiquetas personalizadas</h3><p className="text-xs text-muted-foreground">Disponibles en <b>todos</b> los tipos de correo. Ej. clave <code>[Telefono_Soporte]</code> → valor "(899) 149 1522".</p></div>
        <Button variant="outline" size="sm" onClick={add} data-testid="add-custom-ph"><Plus className="w-4 h-4 mr-1" /> Agregar etiqueta</Button>
      </div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`custom-ph-row-${i}`}>
            <Input className="col-span-4 h-9 font-mono text-xs" placeholder="[Clave]" value={r.clave} onChange={(e) => upd(i, "clave", e.target.value)} data-testid={`custom-ph-clave-${i}`} />
            <Input className="col-span-7 h-9" placeholder="Valor a mostrar" value={r.valor} onChange={(e) => upd(i, "valor", e.target.value)} data-testid={`custom-ph-valor-${i}`} />
            <button className="col-span-1 flex justify-center text-red-500" onClick={() => del(i)} data-testid={`custom-ph-del-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-muted-foreground">Sin etiquetas personalizadas aún.</p>}
      </div>
      <Button onClick={save} data-testid="save-custom-ph"><Save className="w-4 h-4 mr-2" /> Guardar etiquetas</Button>
    </Card>
  );
}

export default function Plantillas() {
  const [terms, setTerms] = useState([]);
  const [nombre, setNombre] = useState("");
  const [contenido, setContenido] = useState("");
  const [branding, setBranding] = useState({});
  const [pdf, setPdf] = useState({});
  const [fonts, setFonts] = useState([]);
  const [customPh, setCustomPh] = useState([]);

  const loadTerms = () => api.get("/term-templates").then((r) => setTerms(r.data));
  const loadBranding = () => api.get("/branding").then((r) => { setBranding(r.data.branding); setPdf(r.data.pdf_config); setCustomPh(r.data.custom_placeholders || []); });
  useEffect(() => { loadTerms(); loadBranding(); api.get("/branding/fonts").then((r) => setFonts(r.data)).catch(() => {}); }, []);
  const customMap = Object.fromEntries((customPh || []).map((p) => [p.clave, p.valor]));

  const saveTerm = async () => {
    if (!nombre || !contenido) return toast.error("Nombre y contenido son obligatorios");
    try { await api.post("/term-templates", { nombre, contenido }); toast.success("Plantilla guardada"); setNombre(""); setContenido(""); loadTerms(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delTerm = async (id) => { await api.delete(`/term-templates/${id}`); toast.success("Eliminada"); loadTerms(); };

  return (
    <div className="space-y-6 max-w-5xl">
      <div><h1 className="text-3xl font-heading font-bold tracking-tight">Plantillas</h1><p className="text-muted-foreground text-sm">Términos, correos por estatus, logo y formato del PDF de cotización.</p></div>
      <Tabs defaultValue="email">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="terms" data-testid="tab-terms"><FileText className="w-4 h-4 mr-2" /> Términos</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email"><Mail className="w-4 h-4 mr-2" /> Correos</TabsTrigger>
          <TabsTrigger value="brand" data-testid="tab-brand"><ImageIcon className="w-4 h-4 mr-2" /> Marca y logo</TabsTrigger>
          <TabsTrigger value="pdf" data-testid="tab-pdf"><FileCog className="w-4 h-4 mr-2" /> Formato PDF</TabsTrigger>
        </TabsList>

        <TabsContent value="terms" className="space-y-4 mt-4">
          <Card className="p-5 rounded-lg space-y-4">
            <h3 className="font-heading font-semibold">Nueva plantilla</h3>
            <div><Label>Nombre</Label><Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej. Envío nacional estándar" data-testid="term-nombre" /></div>
            <div><Label>Contenido</Label><Textarea value={contenido} onChange={(e) => setContenido(e.target.value)} rows={4} placeholder="Ej. Tiempo de envío: 3 días hábiles. Condiciones de pago: fin del siguiente mes. No incluye IVA." data-testid="term-contenido" /></div>
            <Button onClick={saveTerm} data-testid="save-term"><Plus className="w-4 h-4 mr-2" /> Guardar plantilla</Button>
          </Card>
          <div className="space-y-3">
            {terms.map((t) => (
              <Card key={t.id} className="p-4 rounded-lg flex items-start justify-between gap-4" data-testid="term-row">
                <div><div className="font-medium">{t.nombre}</div><div className="text-sm text-muted-foreground whitespace-pre-wrap">{t.contenido}</div></div>
                <Button variant="ghost" size="sm" onClick={() => delTerm(t.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </Card>
            ))}
            {terms.length === 0 && <p className="text-sm text-muted-foreground">Aún no hay plantillas de términos.</p>}
          </div>
        </TabsContent>

        <TabsContent value="email" className="mt-4 space-y-4"><CustomPlaceholderManager list={customPh} reload={loadBranding} /><EmailManager branding={branding} customMap={customMap} /></TabsContent>
        <TabsContent value="brand" className="mt-4"><BrandManager branding={branding} reload={loadBranding} /></TabsContent>
        <TabsContent value="pdf" className="mt-4"><PdfManager pdf={pdf} reload={loadBranding} fonts={fonts} /></TabsContent>
      </Tabs>
    </div>
  );
}
